import { isE2eeEnvelope } from '@buildautomaton/e2ee';
import type { CliE2eeRuntime } from './cli-e2ee-runtime.js';

function parseJsonObject(raw: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return null;
  }
  return null;
}

export function storedStringHasE2eeEnvelope(raw: string): boolean {
  const record = parseJsonObject(raw);
  return record != null && isE2eeEnvelope(record.ee);
}

export function e2eeKeyIdFromStoredString(raw: string): string | null {
  const record = parseJsonObject(raw);
  if (record != null && isE2eeEnvelope(record.ee)) return record.ee.k;
  return null;
}

function decryptStoredE2eeRecord(
  record: Record<string, unknown>,
  e2ee: CliE2eeRuntime | undefined,
): Record<string, unknown> {
  if (!isE2eeEnvelope(record.ee)) return record;
  if (!e2ee) {
    throw new Error(`E2EE_REQUIRED:${record.ee.k}`);
  }
  if (record.ee.k !== e2ee.keyId) {
    throw new Error(`E2EE_KEY_MISMATCH:encrypted=${record.ee.k}:cli=${e2ee.keyId}`);
  }
  return e2ee.decryptMessage(record);
}

/** Format decrypt failures for MCP / agent consumers. */
export function formatCliE2eeDecryptError(error: unknown, e2ee?: CliE2eeRuntime): string {
  const message = error instanceof Error ? error.message : String(error);
  const required = /^E2EE_REQUIRED:(.+)$/.exec(message.trim());
  if (required) {
    const keyId = required[1]?.trim() || 'unknown';
    return `Parent session content is encrypted (key ${keyId}) but this bridge was not started with E2EE certificates. Restart the CLI with your E2EE certificate to read fork parent history.`;
  }
  const mismatch = /^E2EE_KEY_MISMATCH:encrypted=(.+):cli=(.*)$/.exec(message.trim());
  if (mismatch) {
    const encryptedKeyId = mismatch[1]?.trim() || 'unknown';
    const cliKeyId = mismatch[2]?.trim() || e2ee?.keyId || 'none';
    return `Parent session content is encrypted with key ${encryptedKeyId}, but this bridge certificate key is ${cliKeyId}. Use the same E2EE certificate that was used for the parent session.`;
  }
  if (message.includes('E2EE key mismatch')) {
    return `Parent session content could not be decrypted: ${message}`;
  }
  return `Parent session content could not be decrypted: ${message}`;
}

/**
 * Decrypt a stored transcript payload string (plaintext JSON or `{"ee":{...}}` envelope).
 * Matches UI {@link decryptE2eeTranscriptPayload} behavior.
 */
export function decryptCliE2eeTranscriptPayload(payload: string, e2ee: CliE2eeRuntime | undefined): string {
  const record = parseJsonObject(payload);
  if (!record || !isE2eeEnvelope(record.ee)) return payload;
  const decrypted = decryptStoredE2eeRecord(record, e2ee);
  if (typeof decrypted.payload === 'string') return decrypted.payload;
  return JSON.stringify(decrypted.payload ?? decrypted);
}

/**
 * Decrypt a stored field wrapped in an E2EE envelope (e.g. turn `promptText` uses field `prompt`).
 */
export function decryptCliE2eeMessageField(
  payload: string | null | undefined,
  field: string,
  e2ee: CliE2eeRuntime | undefined,
): string | null {
  if (payload == null) return null;
  const record = parseJsonObject(payload);
  if (!record || !isE2eeEnvelope(record.ee)) return payload;
  const decrypted = decryptStoredE2eeRecord(record, e2ee);
  const value = decrypted[field];
  if (value == null) return null;
  return typeof value === 'string' ? value : JSON.stringify(value);
}
